// @ts-nocheck
import React, { useMemo, useEffect } from "react";
import { useState } from "react";
import { useNavigate } from "@tanstack/react-router";
import { useQueryClient } from "@tanstack/react-query";
import {
  Plus,
  Trash2,
  Printer,
  ChevronDown,
  ChevronRight,
  Calendar,
  CheckCircle2,
  FilePlus,
  FileSpreadsheet,
} from "lucide-react";
import {
  DateGroupedTable,
  DateGroupToggleButton,
  extractDateKey,
} from "@/components/shared/DateGroupedTable";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { BulkActionBar, BulkAction } from "@/components/shared/BulkActionBar";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { trpcClient } from "@/lib/trpc";
import { formatMoney, formatDate, cn } from "@/lib/utils";
import { executeBulkAction } from "@/utils/bulkActionUtils";
import UserDisplay from "@/components/shared/UserDisplay";
import { getStatusConfig, getStatusOptions, QuotationStatus } from "@/config/status-registry";
import { PageFilterTags } from "@/components/shared/PageFilterTags";
import { type StatusSidebarItem } from "@/components/shared/StatusSidebar";
import {
  ListPageShell,
  useServerListFilter,
} from "@/components/shared/ListPageShell";
import { useCustomerLookup, useUserLookup } from "@/hooks/useFKLookup";
import type { FilterField } from "@/components/shared/FilterBar";
import {
  InlineFilterRow,
  CHECKBOX_COL_NULL,
} from "@/components/shared/InlineFilterRow";
import {
  downloadExcelTemplate as downloadTemplate,
  type ExcelColumnDef,
} from "@/lib/excel.utils";
import { StackedCell, DeadlineText, contactLine } from "@/components/shared/StackedCell";
import { deadlineStatus } from "@/lib/deadline-status";
import { useExcelExport } from "@/hooks/useExcelExport";
import { ExportImportButtons } from "@/components/shared/ExportImportButtons";
import { ResponsiveDataView } from "@/components/shared/ResponsiveDataView";

import { SortableHeader } from "@/components/shared/SortableHeader";
import { useRowSelection } from "@/hooks/useRowSelection";
import {
  DataTableRow,
  DataTableRowCheckbox,
  DataTableSelectAllCheckbox,
} from "@/components/shared/DataTableRow";
import {
  useTimelineFilter,
  TimelineFilter,
} from "@/components/shared/TimelineFilter";
import { useConfigSuggestions } from "@/hooks/useConfigSuggestions";
import { useRoleAccess } from "@/hooks/useRoleAccess";
import { useConfirm } from "@/providers/ConfirmProvider";
import { emsTabClass } from "@/components/ui/ems-tabs";

const STATUS_ITEMS: StatusSidebarItem[] = Object.entries(QuotationStatus).map(
  ([key, cfg]) => ({
    key,
    label: cfg.label,
    color: cfg.color,
    dotColor: cfg.dotColor,
  }),
);

const BASE_FILTER_FIELDS: FilterField[] = [
  { key: "quotationId", label: "Mã BG", type: "text", placeholder: "BG-..." },
  {
    key: "customerName",
    label: "Khách hàng",
    type: "text",
    placeholder: "Tên KH...",
  },
  {
    key: "productName",
    label: "Sản phẩm",
    type: "text",
    placeholder: "Tên SP...",
  },
  { key: "createdDate", label: "Ngày tạo", type: "date-range" },
  { key: "validUntil", label: "Hiệu lực", type: "date-range" },
  {
    key: "creator.fullName",
    label: "NV Kinh doanh",
    type: "text",
    placeholder: "Tên NV...",
  },
  {
    key: "status",
    label: "Trạng thái",
    type: "combobox-multi",
    options: getStatusOptions("QuotationStatus", false),
  },
  {
    key: "contactPerson",
    label: "Liên hệ",
    type: "text",
    placeholder: "Người liên hệ...",
  },
  { key: "notes", label: "Ghi chú", type: "text", placeholder: "Ghi chú..." },
];

const EXPORT_COLUMNS: ExcelColumnDef[] = [
  { key: "quotationId", label: "Mã BG" },
  { key: "customerName", label: "Khách hàng" },
  { key: "creatorName", label: "NV Kinh doanh" },
  {
    key: "createdDate",
    label: "Ngày tạo",
    type: "date",
    format: (val: any) =>
      val ? new Date(val).toLocaleDateString("vi-VN") : "",
  },
  {
    key: "validUntil",
    label: "Hiệu lực",
    type: "date",
    format: (val: any) =>
      val ? new Date(val).toLocaleDateString("vi-VN") : "",
  },
  { key: "status", label: "Trạng thái" },
  {
    key: "totalAmount",
    label: "Tổng giá trị",
    type: "decimal",
  },
  // Chi tiết sản phẩm
  { key: "productCode", label: "Mã SP" },
  { key: "productName", label: "Tên SP" },
  { key: "unit", label: "ĐVT" },
  { key: "quantity", label: "Số lượng", type: "number" },
  { key: "unitPrice", label: "Đơn giá", type: "decimal" },
  {
    key: "itemTotal",
    label: "Thành tiền",
    type: "decimal",
    format: (_: any, row: any) => (row?.quantity || 0) * (row?.unitPrice || 0),
  },
  { key: "profitMargin", label: "Biên LN (%)", type: "decimal" },
  { key: "notes", label: "Ghi chú" },
];

function fmtDate(d: any) {
  if (!d) return "—";
  try {
    return new Date(d).toLocaleDateString("vi-VN");
  } catch {
    return "—";
  }
}

const getProductSummary = (details: any[]) => {
  if (!details || details.length === 0) return "—";
  return details
    .map(
      (d) =>
        `${d.productName || "—"} (${(d.quantity || 0).toLocaleString("vi-VN")})`,
    )
    .join(", ");
};


/** Dòng phụ trên danh sách báo giá (30/10/2026): người liên hệ · SĐT, Sale lập, tình trạng hiệu lực */
const quotationContact = (q: any) =>
  contactLine(q.contactPerson || q.customer?.contactPerson, q.phone || q.customer?.phone);
const quotationSale = (q: any) => q.salesUser?.fullName || q.creator?.fullName || "";
const quotationValidity = (q: any) =>
  deadlineStatus(q.validUntil, {
    done: (q.orders?.length || 0) > 0,
    doneLabel: "Đã chốt đơn",
    cancelled: ["REJECTED", "CANCELLED"].includes(String(q.status || "").toUpperCase()),
    cancelledLabel: "Khách từ chối",
    todayLabel: "Hết hạn hôm nay",
    overdueLabel: (d) => `Quá hạn ${d} ngày, chưa chốt`,
    soonDays: 5,
  });

export default function QuotationListPage({
  embedded = false,
  customerId,
}: {
  embedded?: boolean;
  customerId?: string;
}) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [showImport, setShowImport] = useState(false);
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());
  const [selectedChildIds, setSelectedChildIds] = useState<Set<string>>(
    new Set(),
  );
  const [isDateGrouped, setIsDateGrouped] = useState(true);
  const confirm = useConfirm();
  const { hasPermission } = useRoleAccess();

  const toggleSelectChild = (id: string) => {
    setSelectedChildIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleSelectAllChild = (childIds: string[]) => {
    setSelectedChildIds((prev) => {
      const next = new Set(prev);
      const allSelected = childIds.every((id) => next.has(id));
      if (allSelected) {
        childIds.forEach((id) => next.delete(id));
      } else {
        childIds.forEach((id) => next.add(id));
      }
      return next;
    });
  };

  const toggleExpand = (id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const { options: customerOptions } = useCustomerLookup();
  const { options: userOptions } = useUserLookup();

  const currencySuggestions = useConfigSuggestions("currency", [
    "VND",
    "USD",
    "JPY",
  ]);

  const filterFields = React.useMemo<FilterField[]>(() => {
    const fields = BASE_FILTER_FIELDS.map((f) => {
      if (f.key === "customerName") {
        return {
          key: "customerId",
          label: "Khách hàng",
          type: "combobox-multi",
          options: customerOptions.map((c) => ({ value: c.id, label: c.label })),
        } as FilterField;
      }
      if (f.key === "creator.fullName") {
        return {
          key: "creatorId",
          label: "NV Kinh doanh",
          type: "combobox-multi",
          options: userOptions.map((u) => ({ value: u.id, label: u.label })),
        } as FilterField;
      }
      return f;
    });
    fields.splice(5, 0, {
      key: "currency",
      label: "Ngoại tệ",
      type: "combobox-multi",
      options: currencySuggestions.map((c) => ({ value: c, label: c })),
    });
    return fields;
  }, [currencySuggestions, customerOptions, userOptions]);

  const { timelineOption, setTimelineOption, dateParams } =
    useTimelineFilter("all");

  const {
    items: allQuotations,
    paginatedItems,
    isLoading,
    searchQuery,
    filterValues,
    serverParams,
    showFilter,
    currentPage,
    pageSize,
    totalItems,
    filtered,
    statusCounts: serverStatusCounts,
    statusFilter,
    onStatusChange,
    onFilterChange,
    onPageChange,
    onPageSizeChange,
    onToggleFilter,
    onClearAll,
    sortField,
    sortOrder,
    handleSort,
  } = useServerListFilter<any>({
    queryKey: "quotations",
    queryFn: (params) => trpcClient.quotations.list.query(params) as any,
    extraParams: {
      ...(dateParams.from
        ? { createdDateFrom: dateParams.from, createdDateTo: dateParams.to }
        : {}),
      ...(customerId ? { customerId } : {}),
    },
  });

  // ── Row selection (AppSheet-style) ──
  const rowIds = paginatedItems.map((q: any) => q.id);
  const rowSelection = useRowSelection(rowIds);
  const {
    selectedIds,
    selectionMode,
    clearSelection,
    selectedCount,
    toggleSelect,
    toggleSelectAll,
    allSelected,
    handleRowSelect,
    wasLongPress,
  } = rowSelection;

  const flattenQuotationsForExport = React.useCallback((quotationsList: any[]) => {
    const data: any[] = [];
    (quotationsList || []).forEach((quote: any) => {
      const creatorName = quote.creator?.fullName || quote.creator?.name || "";
      const customerName = quote.customer?.name || quote.customer?.fullName || quote.customer?.shortName || "";
      if (quote.details && quote.details.length > 0) {
        quote.details.forEach((detail: any) => {
          data.push({
            ...quote,
            id: quote.id,
            customerName,
            creatorName,
            productCode: detail.product?.productCode || detail.productCode || "",
            productName: detail.product?.productName || detail.productName || "",
            specs: detail.specs || "",
            unit: detail.unit || "",
            quantity: detail.quantity || 0,
            unitPrice: detail.quotedPrice || detail.unitPrice || detail.priceVND || 0,
            profitMargin: detail.profitMargin || 0,
            notes: detail.notes || "",
          });
        });
      } else {
        data.push({
          ...quote,
          id: quote.id,
          customerName,
          creatorName,
        });
      }
    });
    return data;
  }, []);

  const { handleExport, isExporting } = useExcelExport({
    filename: "danh_sach_bao_gia",
    sheetName: "Báo Giá",
    columns: EXPORT_COLUMNS,
    queryFn: (params) => trpcClient.quotations.list.query(params),
    currentFilters: serverParams,
    transformData: flattenQuotationsForExport,
    selectedIds,
    idKey: "id",
  });

  const handleImport = async (rows: Record<string, unknown>[]) => {
    let success = 0;
    const errors: string[] = [];

    // Group by quotationId
    const grouped: Record<string, any> = {};
    for (const row of rows) {
      const qId = row.quotationId as string;
      if (!qId) continue;
      if (!grouped[qId]) {
        grouped[qId] = {
          quotationId: qId,
          status: row.status,
          details: [],
        };
      }
      if (row.productCode || row.productName) {
        grouped[qId].details.push({
          productCode: row.productCode,
          productName: row.productName,
          quantity: Number(row.quantity) || 0,
          priceVND: Number(row.unitPrice) || 0,
          notes: row.notes || "",
        });
      }
    }

    const toastId = toast.loading(
      `Đang xử lý ${Object.keys(grouped).length} báo giá...`,
    );
    for (const [id, data] of Object.entries(grouped)) {
      try {
        // Update existing quotation by ID
        await trpcClient.quotations.update.mutate({
          id,
          data: {
            status: data.status,
            details: data.details,
          },
        });
        success++;
      } catch (err: any) {
        errors.push(`${id}: ${err?.message || "Lỗi"}`);
      }
    }

    if (success > 0) {
      await queryClient.invalidateQueries({ queryKey: ["quotations"] });
    } else {
      toast.dismiss(toastId);
    }
    if (errors.length > 0)
      toast.error(
        `${errors.length} dòng lỗi: ${errors.slice(0, 3).join("; ")}`,
      );
  };

  const handleDelete = async (id: string) => {
    try {
      await trpcClient.quotations.delete.mutate({ id });
      toast.success("Đã xóa bản ghi thành công.");

      await queryClient.invalidateQueries({ queryKey: ["quotations"] });
    } catch (err: any) {
      toast.error(err?.message || "Xoá thất bại");
    }
  };

  // User requested to remove auto-expand logic on first load. Default should be collapsed.
  // const [lastAutoExpandDataId, setLastAutoExpandDataId] = React.useState<string>('');
  // React.useEffect(() => {
  //     if (paginatedItems && paginatedItems.length > 0) {
  //         const dataId = paginatedItems.map((i: any) => i.id).join(',');
  //         if (dataId !== lastAutoExpandDataId) {
  //             if (currentPage === 1) {
  //                 setExpandedIds(new Set(paginatedItems.map((o: any) => o.id)));
  //             } else {
  //                 setExpandedIds(new Set());
  //             }
  //             setLastAutoExpandDataId(dataId);
  //         }
  //     }
  // }, [paginatedItems, currentPage, lastAutoExpandDataId]);

  const getBulkActions = (): BulkAction[] => {
    if (!selectedCount) return [];
    const actions: BulkAction[] = [];
    const selectedItems = paginatedItems.filter((q: any) => selectedIds.has(q.id));

    if (hasPermission("quotations", "approve")) {
      const canApprove = selectedItems.length > 0 && selectedItems.every((q: any) => ["SENT", "PROCESSING", "ESTIMATED"].includes(q.status)); // các bước được phép sang Đã duyệt
      if (canApprove) {
        actions.push({
          label: "Phê duyệt",
          icon: <CheckCircle2 className="w-3.5 h-3.5" />,
          onClick: async () => {
            await executeBulkAction({
              selectedIds: Array.from(selectedIds),
              bulkMutationFn: (ids) => trpcClient.quotations.bulkUpdateStatus.mutate({ ids: ids as string[], status: "APPROVED" }),
              onSuccess: () => {
                clearSelection();
                queryClient.invalidateQueries({ queryKey: ["quotations"] });
              }
            });
          }
        });
      }
    }

    if (selectedCount === 1) {
      const q = selectedItems[0];
      if (q && q.status === "APPROVED") {
        actions.push({
          label: "Tạo Đơn Hàng",
          icon: <FilePlus className="w-3.5 h-3.5" />,
          onClick: () => {
            navigate({ to: "/orders/create", search: { quotationId: q.id } as any });
          }
        });
      }
    }

    if (hasPermission("quotations", "delete")) {
      actions.push({
        label: "Xoá",
        icon: <Trash2 className="w-3.5 h-3.5" />,
        onClick: async () => {
          if (
            !(await confirm(
              `Bạn có chắc muốn xoá ${selectedCount} báo giá đã chọn?`,
            ))
          )
            return;

          await executeBulkAction({
            confirmFn: confirm,
            selectedIds: Array.from(selectedIds),
            bulkMutationFn: (ids) => trpcClient.quotations.bulkDelete.mutate({ ids: ids as string[] }),
            onSuccess: () => {
              clearSelection();
              queryClient.invalidateQueries({ queryKey: ["quotations"] });
            },
            messages: {
              loading: `Đang xoá ${selectedCount} báo giá...`,
              success: `Đã xoá thành công ${selectedCount} báo giá`,
            },
          });
        },
        variant: "destructive",
      });
    }
    return actions;
  };

  const COLS = 8;

  const filterTags = React.useMemo(() => {
    return STATUS_ITEMS.map((item) => {
      const sc = serverStatusCounts?.find(
        (s: any) =>
          s.status?.toString()?.toUpperCase() === item.key?.toUpperCase(),
      );
      return {
        ...item,
        count: sc ? sc.count : 0,
      };
    });
  }, [serverStatusCounts]);
  return (
    <>
      <ListPageShell
        trashModule="quotations"
        embedded={embedded}
        tabs={
          <div className="flex items-center gap-4">
            <button
              type="button"
              className={emsTabClass(true, { size: "sm" })}
            >
              <FileSpreadsheet className="w-3.5 h-3.5 text-indigo-600" />
              <span>Danh Sách Báo Giá</span>
              <span className="px-1.5 py-0.2 rounded-full text-[10px] font-bold bg-primary/10 text-primary tabular-nums">
                {totalItems}
              </span>
            </button>
          </div>
        }
        headerFilters={
          <PageFilterTags
            items={filterTags}
            selectedKeys={
              statusFilter
                ? Array.isArray(statusFilter)
                  ? statusFilter
                  : [statusFilter]
                : []
            }
            onSelect={onStatusChange}
            dropdownLabel="Trạng thái BG"
          />
        }
        dateFilter={
          <div className="flex items-center gap-2">
            <DateGroupToggleButton
              isGrouped={isDateGrouped}
              onToggle={() => setIsDateGrouped(!isDateGrouped)}
            />
            <TimelineFilter
              value={timelineOption}
              onChange={setTimelineOption}
            />
          </div>
        }
        unitLabel="BG"
        actions={
          <div className="flex items-center gap-2">
            <ExportImportButtons
              onExport={handleExport}
              exportLoading={isExporting}
              onDownloadTemplate={() => downloadTemplate(EXPORT_COLUMNS, "mau_bao_gia")}
            >
              {hasPermission("quotations", "create") && (
                <Button
                  size="sm"
                  className="h-8 text-xs bg-indigo-600 hover:bg-indigo-700 text-white font-medium"
                  onClick={() => navigate({ to: "/quotations/create" })}
                >
                  <Plus size={14} className="mr-1" /> Tạo BG
                </Button>
              )}
            </ExportImportButtons>
          </div>
        }
        filterFields={filterFields}
        filterValues={filterValues}
        showFilter={showFilter}
        onToggleFilter={onToggleFilter}
        onClearAll={onClearAll}
        currentPage={currentPage}
        pageSize={pageSize}
        totalItems={totalItems}
        onPageChange={onPageChange}
        onPageSizeChange={onPageSizeChange}
      >
        <ResponsiveDataView
          items={paginatedItems}
          emptyState={
            searchQuery ? "Không tìm thấy báo giá phù hợp" : "Chưa có báo giá"
          }
          renderMobileCard={(q: any) => {
            const st = getStatusConfig("QuotationStatus", q.status);
            return (
              <div
                className={cn(
                  "flex flex-col bg-white border rounded-xl shadow-sm overflow-hidden active:scale-[0.99] transition-transform",
                  selectedIds.has(q.id)
                    ? "border-indigo-400 ring-1 ring-indigo-400"
                    : "border-slate-200",
                )}
                onClick={async (e) => {
                  if (selectionMode) {
                    handleRowSelect(q.id, e);
                    return;
                  }
                  navigate({
                    to: "/quotations/$quotationId",
                    params: { quotationId: q.id } as any,
                  });
                }}
              >
                <div className="flex items-center justify-between p-3 border-b border-slate-100 bg-slate-50/50">
                  <div className="flex items-center gap-2">
                    {selectionMode && (
                      <div onClick={(e) => e.stopPropagation()}>
                        <Checkbox
                          checked={selectedIds.has(q.id)}
                          onCheckedChange={() => toggleSelect(q.id)}
                        />
                      </div>
                    )}
                    <div className="flex flex-col">
                      <span className="font-bold text-slate-800 text-sm">
                        {q.quotationId}
                      </span>
                      {q.version && q.version > 1 && (
                        <span className="text-[10px] text-slate-500 leading-none mt-0.5">
                          v{q.version}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="flex flex-col items-end gap-1">
                    <Badge
                      variant="outline"
                      className={cn(
                        "text-[10px] px-2 py-0.5 border-none font-bold",
                        st.color,
                      )}
                    >
                      {st.label}
                    </Badge>
                    {q.isNonMoq && (
                      <Badge className="text-[9px] px-1.5 py-0 bg-purple-600 hover:bg-purple-700 text-white border-none">
                        NON-MOQ
                      </Badge>
                    )}
                  </div>
                </div>

                <div className="p-3 flex flex-col gap-2.5">
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex flex-col">
                      <span className="text-[10px] text-slate-500 uppercase tracking-wider mb-0.5">
                        Khách hàng
                      </span>
                      <span className="text-sm font-semibold text-slate-900 leading-tight">
                        {q.customer?.shortName ||
                          q.customer?.name ||
                          q.customerName ||
                          "—"}
                      </span>
                      {quotationContact(q) && (
                        <span className="mt-0.5 text-[11px] text-slate-500">{quotationContact(q)}</span>
                      )}
                    </div>
                    <div className="flex flex-col items-end shrink-0">
                      <span className="text-[10px] text-slate-500 uppercase tracking-wider mb-0.5">
                        Hiệu lực đến
                      </span>
                      <span className="text-sm font-medium text-slate-700 font-mono">
                        {fmtDate(q.validUntil)}
                      </span>
                      <span className="text-[11px]"><DeadlineText status={quotationValidity(q)} /></span>
                    </div>
                  </div>

                  <div className="flex items-center justify-between mt-1 pt-2.5 border-t border-slate-100 border-dashed">
                    <div className="flex flex-col">
                      <span className="text-[10px] text-slate-500 uppercase tracking-wider mb-0.5">
                        Ngày tạo
                      </span>
                      <span className="text-sm font-medium text-slate-700 font-mono">
                        {fmtDate(q.createdDate || q.createdAt)}
                      </span>
                      {quotationSale(q) && <span className="text-[11px] text-slate-500">{quotationSale(q)}</span>}
                    </div>
                    <div className="flex flex-col items-end">
                      <span className="text-[10px] text-slate-500 uppercase tracking-wider mb-0.5">
                        Tổng giá trị
                      </span>
                      <span className="font-mono font-bold text-sm text-slate-900">
                        {formatMoney(
                          q.details?.reduce(
                            (s: number, d: any) =>
                              s +
                              Number(d.quantity || 0) * Number(d.priceVND || 0),
                            0,
                          ) || 0,
                        )}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            );
          }}
        >
          <table
            className={cn(
              "w-full text-xs caption-bottom apv-compact-table table-fixed min-w-[1120px]",
              selectionMode && "apv-select-mode",
            )}
          >
            <colgroup>
              <col className="apv-col-checkbox" />
              <col className="w-[180px]" />
              <col className="w-[220px]" />
              <col className="w-[240px]" />
              <col className="w-[110px]" />
              <col className="w-[120px]" />
              <col className="w-[130px]" />
              <col className="w-[80px]" />
            </colgroup>
            <thead className="apv-table-header">
              <tr>
                <DataTableSelectAllCheckbox rowSelection={rowSelection} />
                <SortableHeader
                  label="Mã BG"
                  field="quotationId"
                  currentField={sortField}
                  currentOrder={sortOrder}
                  onSort={handleSort}
                  className="text-left apv-col-code"
                />
                <SortableHeader
                  label="Khách hàng / Liên hệ"
                  field="customer.shortName"
                  currentField={sortField}
                  currentOrder={sortOrder}
                  onSort={handleSort}
                  className="text-left"
                />
                <th className="text-left px-2 py-1.5">Sản phẩm</th>
                <SortableHeader
                  label="Ngày tạo"
                  field="createdDate"
                  currentField={sortField}
                  currentOrder={sortOrder}
                  onSort={handleSort}
                  className="text-right"
                />
                <SortableHeader
                  label="Hiệu lực"
                  field="validUntil"
                  currentField={sortField}
                  currentOrder={sortOrder}
                  onSort={handleSort}
                  className="text-right"
                />
                <SortableHeader
                  label="Giá trị (VNĐ)"
                  field="totalAmount"
                  currentField={sortField}
                  currentOrder={sortOrder}
                  onSort={handleSort}
                  className="text-right"
                />
                <th className="text-right px-2 py-1.5 apv-col-actions"></th>
              </tr>
              {showFilter && (
                <InlineFilterRow
                  columns={[
                    CHECKBOX_COL_NULL, // checkbox
                    { key: "quotationId" },
                    { key: "customerName" },
                    { key: "productName" },
                    { key: "createdDate" },
                    { key: "validUntil" },
                    null, // giá trị
                    null, // Action
                  ]}
                  filterFields={filterFields}
                  filterValues={filterValues}
                  onFilterChange={onFilterChange}
                />
              )}
            </thead>
            <tbody>
              {isLoading ? (
                Array.from({ length: 10 }).map((_, i) => (
                  <tr key={i}>
                    {Array.from({ length: COLS }).map((_, j) => (
                      <td key={j} className="px-2 py-2">
                        <Skeleton className="h-3 w-14" />
                      </td>
                    ))}
                  </tr>
                ))
              ) : (
                <DateGroupedTable<any>
                  items={paginatedItems}
                  isGrouped={isDateGrouped}
                  getDateKey={(q) =>
                    extractDateKey(q.createdDate || q.quotationDate || q.createdAt)
                  }
                  emptyState={
                    <tr>
                      <td
                        colSpan={COLS}
                        className="text-center text-slate-600 py-12 text-sm"
                      >
                        {searchQuery
                          ? "Không tìm thấy báo giá phù hợp"
                          : "Chưa có báo giá"}
                      </td>
                    </tr>
                  }
                  renderGroupHeader={(group) => {
                    const groupTotalAmount = group.items.reduce((s, q: any) => {
                      const amount =
                        Number(q.totalAmount) ||
                        q.details?.reduce(
                          (ds: number, d: any) =>
                            ds +
                            Number(d.quantity || 0) *
                              Number(d.priceVND || d.unitPrice || 0),
                          0,
                        ) ||
                        0;
                      return s + amount;
                    }, 0);
                    const groupTotalLines = group.items.reduce(
                      (s, q: any) => s + (q.details?.length || 0),
                      0,
                    );

                    return (
                      <tr
                        key={`group-${group.dateKey}`}
                        className={cn(
                          "border-y font-semibold text-xs transition-colors select-none",
                          group.isToday
                            ? "bg-[#eef2ff] hover:bg-[#e0e7ff] border-indigo-200"
                            : "bg-[#f1f5f9] hover:bg-[#e2e8f0] border-slate-300",
                        )}
                      >
                        {/* ColSpan 2: Checkbox + Mã BG */}
                        <td
                          colSpan={2}
                          className="py-2.5 px-3 font-bold text-slate-900 border-r border-slate-200"
                          style={{
                            backgroundColor: group.isToday ? "#eef2ff" : "#f1f5f9",
                            isolation: "isolate",
                          }}
                        >
                          <div className="flex items-center gap-1.5">
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-5 w-5 p-0 text-slate-600 hover:text-slate-900 shrink-0"
                              onClick={group.toggleCollapse}
                              title={group.isCollapsed ? "Bung ra" : "Thu gọn"}
                            >
                              {group.isCollapsed ? (
                                <ChevronRight size={15} />
                              ) : (
                                <ChevronDown size={15} />
                              )}
                            </Button>
                            <Calendar className="w-3.5 h-3.5 text-indigo-600 shrink-0" />
                            <span className="text-[12px] font-bold text-slate-900 whitespace-nowrap">
                              {group.formattedDate}
                            </span>
                          </div>
                        </td>

                        {/* ColSpan 2: Khách hàng & Sản phẩm */}
                        <td colSpan={2} className="py-2 px-2 text-slate-700">
                          <div className="flex items-center gap-2">
                            <Badge
                              variant="outline"
                              className="bg-white/90 text-slate-800 border-slate-300 text-[11px] font-bold px-2 py-0.5"
                            >
                              {group.count} báo giá
                            </Badge>
                            {groupTotalLines > 0 && (
                              <span className="text-[11px] text-slate-500 font-normal">
                                ({groupTotalLines} dòng SP)
                              </span>
                            )}
                            {group.isToday && (
                              <Badge className="bg-indigo-600 text-white text-[10px] font-bold px-1.5 py-0 border-0">
                                HÔM NAY
                              </Badge>
                            )}
                            {group.isCollapsed && (
                              <span className="text-[11px] text-slate-500 font-normal italic">
                                (Đang thu gọn - bấm để xem chi tiết)
                              </span>
                            )}
                          </div>
                        </td>

                        {/* ColSpan 2: Ngày tạo & Hiệu lực */}
                        <td colSpan={2} className="py-2 px-2 text-slate-400 text-center text-xs">
                          —
                        </td>

                        {/* Col 7: Giá trị VNĐ */}
                        <td className="text-right py-2 px-2 font-mono font-bold text-slate-900 text-xs">
                          {formatMoney(groupTotalAmount)}
                        </td>

                        {/* Col 8: Action */}
                        <td className="py-2 px-2 text-slate-400 text-right text-xs">
                          —
                        </td>
                      </tr>
                    );
                  }}
                  renderRow={(q: any) => {
                  const getStatusBg = (st: string) => {
                    switch (st) {
                      case "DRAFT":
                        return "bg-slate-50/50";
                      case "SENT":
                        return "bg-blue-50/30";
                      case "APPROVED":
                        return "bg-emerald-50/40";
                      case "REJECTED":
                        return "bg-red-50/30";
                      case "EXPIRED":
                        return "bg-orange-50/30";
                      default:
                        return "bg-white";
                    }
                  };
                  const rowBg = getStatusBg(q.status);

                  return (
                    <React.Fragment key={q.id}>
                      <DataTableRow
                        id={q.id}
                        rowSelection={rowSelection}
                        className={cn(
                          "cursor-pointer hover:bg-slate-50/80 transition-colors border-b-0",
                          selectedIds.has(q.id) ? "bg-indigo-50/60" : rowBg,
                        )}
                        onClick={() =>
                          navigate({
                            to: "/quotations/$quotationId",
                            params: { quotationId: q.id },
                          })
                        }
                      >
                        <DataTableRowCheckbox
                          id={q.id}
                          rowSelection={rowSelection}
                        />
                        <td className="px-2 py-1 w-[180px] apv-col-code">
                          <div className="flex items-start gap-1.5 mt-0.5">
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-5 w-5 rounded p-0 m-0 text-slate-500 shrink-0"
                              onClick={(e) => toggleExpand(q.id, e)}
                            >
                              {expandedIds.has(q.id) ? (
                                <ChevronDown size={14} />
                              ) : (
                                <ChevronRight size={14} />
                              )}
                            </Button>
                            <div className="flex flex-col items-start gap-1">
                              <div className="flex items-baseline gap-1">
                                <span className="font-semibold text-slate-900 leading-tight whitespace-nowrap">
                                  {q.quotationId}
                                </span>
                                {q.version && q.version > 1 && (
                                  <span className="text-[10px] text-slate-500 leading-none">
                                    v{q.version}
                                  </span>
                                )}
                              </div>
                              {(() => {
                                const st = getStatusConfig(
                                  "QuotationStatus",
                                  q.status,
                                );
                                return (
                                  <div className="flex flex-wrap items-center gap-1 mt-0.5">
                                    <Badge
                                      variant="outline"
                                      className={cn(
                                        "text-[9px] px-1.5 py-0 border-none font-bold",
                                        st.color,
                                      )}
                                    >
                                      {st.label}
                                    </Badge>
                                    {q.isNonMoq && (
                                      <Badge className="text-[9px] px-1.5 py-0 bg-purple-600 hover:bg-purple-700 text-white border-none">
                                        NON-MOQ
                                      </Badge>
                                    )}
                                  </div>
                                );
                              })()}
                            </div>
                          </div>
                        </td>
                        <td className="px-2 py-1 min-w-[200px] break-words whitespace-normal leading-tight text-[12px]">
                          <div
                            className="font-semibold text-slate-900 leading-tight"
                            title={
                              q.customer?.shortName ||
                              q.customer?.name ||
                              q.customerName ||
                              "—"
                            }
                          >
                            {q.customer?.shortName ||
                              q.customer?.name ||
                              q.customerName ||
                              "—"}
                          </div>
                          {quotationContact(q) && (
                            <div className="mt-0.5 text-[11px] text-slate-500 truncate" data-testid="quotation-row-contact">
                              {quotationContact(q)}
                            </div>
                          )}
                        </td>
                        <td className="px-2 py-1">
                          {(() => {
                            if (!q.details || q.details.length === 0) {
                              return <span className="text-slate-400">—</span>;
                            }
                            const visible = q.details.slice(0, 2);
                            const remaining = q.details.length - 2;
                            return (
                              <div className="flex flex-col gap-0.5 text-xs text-slate-700">
                                {visible.map((d: any, idx: number) => (
                                  <div
                                    key={idx}
                                    className="truncate"
                                    title={`${d.productName || "—"} (${(d.quantity || 0).toLocaleString("vi-VN")})`}
                                  >
                                    <span className="font-medium">
                                      {d.productName || "—"}
                                    </span>
                                    <span className="text-slate-500 ml-1 text-[11px]">
                                      (
                                      {(d.quantity || 0).toLocaleString(
                                        "vi-VN",
                                      )}
                                      )
                                    </span>
                                  </div>
                                ))}
                                {remaining > 0 && (
                                  <div
                                    className="text-[10px] text-indigo-500 font-semibold mt-0.5 hover:underline"
                                    onClick={(e) => toggleExpand(q.id, e)}
                                  >
                                    + {remaining} sản phẩm khác
                                  </div>
                                )}
                              </div>
                            );
                          })()}
                        </td>

                        <td className="px-2 py-1 text-right">
                          <StackedCell
                            align="right"
                            primary={fmtDate(q.createdDate || q.createdAt)}
                            primaryClassName="font-mono text-[11px] text-slate-700"
                            secondary={quotationSale(q)}
                            testId="quotation-row-sale"
                          />
                        </td>
                        <td className="px-2 py-1 text-right">
                          <StackedCell
                            align="right"
                            primary={fmtDate(q.validUntil)}
                            primaryClassName="font-mono text-[11px] text-slate-700"
                            secondary={<DeadlineText status={quotationValidity(q)} testId="quotation-row-validity" />}
                            title={q.orders?.length ? `Đơn hàng: ${q.orders.map((o: any) => o.orderId).join(", ")}` : undefined}
                          />
                        </td>
                        <td className="px-2 py-1 text-right w-[120px]">
                          <div className="font-mono font-bold text-slate-900 leading-tight">
                            {formatMoney(
                              q.details?.reduce(
                                (s: number, d: any) =>
                                  s +
                                  Number(d.quantity || 0) *
                                    Number(d.priceVND || d.unitPrice || 0),
                                0,
                              ) || 0,
                            )}
                          </div>
                        </td>
                        <td
                          className="px-2 py-1.5 text-right apv-col-actions"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <div className="flex items-center justify-end gap-0 apv-row-actions">
                            {hasPermission("quotations", "delete") && (
                              <AlertDialog>
                                <AlertDialogTrigger asChild>
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    className="h-6 w-6"
                                  >
                                    <Trash2
                                      size={12}
                                      className="text-destructive"
                                    />
                                  </Button>
                                </AlertDialogTrigger>
                                <AlertDialogContent>
                                  <AlertDialogHeader>
                                    <AlertDialogTitle>
                                      Xác nhận xoá
                                    </AlertDialogTitle>
                                    <AlertDialogDescription>
                                      Xoá báo giá {q.quotationId}?
                                    </AlertDialogDescription>
                                  </AlertDialogHeader>
                                  <AlertDialogFooter>
                                    <AlertDialogCancel>Huỷ</AlertDialogCancel>
                                    <AlertDialogAction
                                      onClick={() => handleDelete(q.id)}
                                      className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                                    >
                                      Xoá
                                    </AlertDialogAction>
                                  </AlertDialogFooter>
                                </AlertDialogContent>
                              </AlertDialog>
                            )}
                          </div>
                        </td>
                      </DataTableRow>
                      {expandedIds.has(q.id) && (
                        <tr className="bg-slate-50/60 border-b border-slate-100">
                          <td colSpan={8} className="p-3 pl-12 pr-6">
                            <div className="bg-white rounded-lg border border-slate-200/80 shadow-sm overflow-hidden">
                              <div className="px-4 py-2 border-b border-slate-100 bg-slate-50/80 flex items-center justify-between">
                                <span className="font-bold text-slate-800 text-[11px] uppercase tracking-wider">
                                  Chi tiết báo giá: {q.quotationId}
                                </span>
                              </div>
                              <div className="overflow-x-auto">
                                <table className="w-full text-[11px] text-slate-600 min-w-[700px] table-fixed">
                                  <colgroup>
                                    <col className="apv-col-checkbox" />
                                    <col className="w-[50px]" />
                                    <col className="w-[120px]" />
                                    <col className="min-w-[150px]" />
                                    <col className="w-[80px]" />
                                    <col className="w-[90px]" />
                                    <col className="w-[110px]" />
                                    <col className="w-[110px]" />
                                    <col className="w-auto" />
                                  </colgroup>
                                  <thead className="bg-slate-50 text-slate-500 font-semibold border-b border-slate-200">
                                    <tr>
                                      <th
                                        className="apv-col-checkbox w-[40px] px-2 py-2 text-center"
                                        onClick={(e) => e.stopPropagation()}
                                      >
                                        <Checkbox
                                          checked={
                                            q.details?.length > 0 &&
                                            q.details.every(
                                              (d: any, i: number) =>
                                                selectedChildIds.has(
                                                  d.id ||
                                                    d.quotationDetailId ||
                                                    String(i),
                                                ),
                                            )
                                          }
                                          onCheckedChange={() =>
                                            toggleSelectAllChild(
                                              q.details?.map(
                                                (d: any, i: number) =>
                                                  d.id ||
                                                  d.quotationDetailId ||
                                                  String(i),
                                              ) || [],
                                            )
                                          }
                                        />
                                      </th>
                                      <th className="px-2 py-2 text-center">
                                        STT
                                      </th>
                                      <th className="px-2 py-2 text-left">
                                        Mã SP
                                      </th>
                                      <th className="px-2 py-2 text-left">
                                        Tên Sản phẩm
                                      </th>
                                      <th className="px-2 py-2 text-left">
                                        ĐVT
                                      </th>
                                      <th className="px-2 py-2 text-right">
                                        Số lượng
                                      </th>
                                      <th className="px-2 py-2 text-right">
                                        Đơn giá
                                      </th>
                                      <th className="px-2 py-2 text-right">
                                        Thành tiền
                                      </th>
                                      <th className="px-2 py-2 text-left">
                                        Ghi chú
                                      </th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {!q.details || q.details.length === 0 ? (
                                      <tr>
                                        <td
                                          colSpan={8}
                                          className="text-center py-4 text-slate-400"
                                        >
                                          Không có chi tiết sản phẩm
                                        </td>
                                      </tr>
                                    ) : (
                                      q.details.map((d: any, i: number) => {
                                        const qty = d.quantity || 0;
                                        const price =
                                          d.priceVND || d.unitPrice || 0;
                                        const total = qty * price;
                                        return (
                                          <tr
                                            key={i}
                                            className="border-b border-slate-100/50 last:border-0 hover:bg-slate-50"
                                          >
                                            <td
                                              className="apv-col-checkbox px-2 py-2"
                                              onClick={(e) =>
                                                e.stopPropagation()
                                              }
                                            >
                                              <Checkbox
                                                checked={selectedChildIds.has(
                                                  d.id ||
                                                    d.quotationDetailId ||
                                                    String(i),
                                                )}
                                                onCheckedChange={() =>
                                                  toggleSelectChild(
                                                    d.id ||
                                                      d.quotationDetailId ||
                                                      String(i),
                                                  )
                                                }
                                              />
                                            </td>
                                            <td className="px-2 py-1.5 text-center text-slate-400 font-mono">
                                              {i + 1}
                                            </td>
                                            <td className="px-2 py-1.5 font-mono text-slate-500 truncate">
                                              {d.productCode || "—"}
                                            </td>
                                            <td className="px-2 py-1.5 text-slate-700 text-xs break-words">
                                              {d.productName || "—"}
                                            </td>
                                            <td className="px-2 py-1.5 text-slate-500">
                                              {d.unit || "—"}
                                            </td>
                                            <td className="text-right px-2 py-1.5 font-mono">
                                              {qty.toLocaleString("vi-VN")}
                                            </td>
                                            <td className="text-right px-2 py-1.5 font-mono">
                                              {formatMoney(price)}
                                            </td>
                                            <td className="text-right px-2 py-1.5 font-mono font-bold text-slate-700">
                                              {formatMoney(total)}
                                            </td>
                                            <td className="px-2 py-1.5 text-[9px] text-slate-500 italic break-words">
                                              {d.notes || "—"}
                                            </td>
                                          </tr>
                                        );
                                      })
                                    )}
                                  </tbody>
                                </table>
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                }}
              />
              )}

              {/* Page Subtotal Footer Row */}
              {!isLoading && paginatedItems.length > 0 && (() => {
                const pageTotalAmount = paginatedItems.reduce(
                  (s: number, q: any) => {
                    const amount =
                      Number(q.totalAmount) ||
                      q.details?.reduce(
                        (ds: number, d: any) =>
                          ds +
                          Number(d.quantity || 0) *
                            Number(d.priceVND || d.unitPrice || 0),
                        0,
                      ) ||
                      0;
                    return s + amount;
                  },
                  0,
                );
                const pageTotalLines = paginatedItems.reduce(
                  (s: number, q: any) => s + (q.details?.length || 0),
                  0,
                );

                return (
                  <tr
                    className="border-t-2 border-slate-300 font-bold text-xs"
                    style={{ backgroundColor: "#f8fafc", isolation: "isolate" }}
                  >
                    <td
                      colSpan={2}
                      className="py-3 px-3 text-slate-900 border-r border-slate-200"
                    >
                      <div className="flex items-center gap-2">
                        <span className="font-bold uppercase tracking-wider text-[11px] text-slate-600">
                          Tổng Trang:
                        </span>
                        <Badge
                          variant="outline"
                          className="bg-white text-slate-900 border-slate-300 font-bold px-2 py-0.5"
                        >
                          {paginatedItems.length} báo giá
                        </Badge>
                      </div>
                    </td>
                    <td colSpan={2} className="py-3 px-2 text-slate-600 text-xs">
                      {pageTotalLines > 0
                        ? `Tổng ${pageTotalLines} dòng sản phẩm trên trang`
                        : "Tổng giá trị báo giá trên trang"}
                    </td>
                    <td
                      colSpan={2}
                      className="py-3 px-2 text-slate-400 text-center text-xs"
                    >
                      —
                    </td>
                    <td className="text-right py-3 px-2 font-mono font-bold text-slate-900 text-xs">
                      {formatMoney(pageTotalAmount)}
                    </td>
                    <td className="py-3 px-2 text-slate-400 text-right text-xs">
                      —
                    </td>
                  </tr>
                );
              })()}
            </tbody>
          </table>
        </ResponsiveDataView>
      </ListPageShell>
      <BulkActionBar
        selectedCount={selectedCount}
        onClearSelection={clearSelection}
        actions={getBulkActions()}
      />
    </>
  );
}
